import { BiDashboardGhost } from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { BiPreviewCardState } from '@euler/model/bi/change-set';
import {
  BiCardView,
  BiDashboardLayout,
  BiDashboardView,
} from '@euler/model/bi/dashboard';
import { BiDraftView } from '@euler/model/bi/draft';

/** The dashboard as a pending change set would leave it. */
export interface BiGhostCards {
  layout: BiDashboardLayout;
  /** Every card the layout places, new ones included once their draft is read. */
  cards: BiCardView[];
  states: ReadonlyMap<string, BiPreviewCardState>;
  /** Titles of the cards it removes (they are not in the layout). */
  removed: string[];
}

/**
 * The cards to draw for a ghost: the dashboard's own cards where they will
 * be, and each new card from its draft. The grid draws card views, so a
 * new card stands in as one under its draft id, which is how the preview
 * layout names it.
 */
export function biGhostCards(
  dashboard: BiDashboardView,
  ghost: BiDashboardGhost,
  drafts: ReadonlyMap<string, BiDraftView>,
): BiGhostCards {
  const existing = new Map(dashboard.cards.map(card => [card.id, card]));
  const cards: BiCardView[] = [];
  const states = new Map<string, BiPreviewCardState>();
  const removed: string[] = [];
  for (const card of ghost.preview.cards) {
    states.set(card.id, card.state);
    if (card.state === BiPreviewCardState.Removed) {
      removed.push(card.title);
      continue;
    }
    if (card.state === BiPreviewCardState.Added) {
      const draft = card.draftId ? drafts.get(card.draftId) : undefined;
      if (draft)
        cards.push({
          id: card.id,
          title: card.title,
          spec: draft.spec,
          viz: draft.viz,
          sourceDraftId: draft.id,
          createdBy: 0,
          createdAt: draft.createdAt,
          updatedAt: draft.createdAt,
        });
      continue;
    }
    // An updated card reads under the title it will have.
    const own = existing.get(card.id);
    if (own)
      cards.push(
        own.title === card.title ? own : { ...own, title: card.title },
      );
  }
  return { layout: ghost.preview.layout, cards, states, removed };
}
